import * as Yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import { regularExpressions } from '~/constants/regularExpressions.ts';
import { IDENTITY_DOCS as generalInfoDoc, IdentityDocumentType } from '~/constants/docs.ts';
import { parseDate } from '~/helpers/utils';
import { TAutocompleteOption } from '~/components/AutocompleteField/types';

export const transformDate = (value: Date | undefined, originalValue: Yup.AnyObject) => {
  if (originalValue && typeof originalValue === 'string') {
    const parsedDate = parseDate(originalValue);
    return parsedDate || new Date('');
  }
  return value;
};

export const baseIdentificationNumberSchema = Yup.string()
  .required(errorMessages.FIELD_REQUIRED)
  .matches(regularExpressions.IDENTIFICATION_NUMBER, errorMessages.MUST_BE_NUMBER)
  .test(
    'not-default-values',
    errorMessages.INVALID_INPUT_DATA,
    (value: string | undefined) =>
      !value || !regularExpressions.INVALID_IDENTIFICATION_NUMBERS.test(value)
  );

export const requiredDateSchema = Yup.date()
  .transform(transformDate)
  .typeError(errorMessages.CORRECT_DATE_FORMAT)
  .required(errorMessages.FIELD_REQUIRED);

export const requiredOptionSchema = Yup.object().nullable().required(errorMessages.FIELD_REQUIRED);

export const documentSeriesSchema = Yup.string()
  .nullable()
  .when('documentType', {
    is: (docType: TAutocompleteOption | null) =>
      docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassport]),
    then: (schema) =>
      schema
        .matches(regularExpressions.MIXED_TWO_LETTERS, errorMessages.DOCUMENT_TWO_CYRILLIC_LETTERS)
        .required(errorMessages.FIELD_REQUIRED),
    otherwise: (schema) =>
      schema.when('documentType', {
        is: (docType: TAutocompleteOption | null) =>
          docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianForeignPassport]),
        then: (schema) =>
          schema
            .matches(regularExpressions.LATIN_TWO_LETTERS, errorMessages.DOCUMENT_TWO_LATIN_LETTERS)
            .required(errorMessages.FIELD_REQUIRED),
        otherwise: (schema) => schema.notRequired(),
      }),
  });

export const documentNumberSchema = Yup.string().when(
  ['documentType'],
  ([documentType], schema: Yup.StringSchema) => {
    const docType = documentType as TAutocompleteOption | null;
    if (docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassport])) {
      return schema
        .matches(regularExpressions.SIX_DIGITS, errorMessages.DOCUMENT_SIX_DIGITS)
        .required(errorMessages.FIELD_REQUIRED);
    } else if (
      docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianForeignPassport])
    ) {
      return schema
        .matches(regularExpressions.SIX_DIGITS, errorMessages.DOCUMENT_SIX_DIGITS)
        .required(errorMessages.FIELD_REQUIRED);
    } else if (
      docType?.value ===
      String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassportPlastic])
    ) {
      return schema
        .matches(regularExpressions.NINE_DIGITS, errorMessages.DOCUMENT_NINE_DIGITS)
        .required(errorMessages.FIELD_REQUIRED);
    }
    return schema.required(errorMessages.FIELD_REQUIRED);
  }
);

export const issuingAuthoritySchema = Yup.string().when(
  ['documentType'],
  ([documentType], schema: Yup.StringSchema) => {
    const docType = documentType as TAutocompleteOption | null;
    if (docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassport])) {
      return schema
        .matches(regularExpressions.AUTHORITY_LETTERS, errorMessages.DOCUMENT_AUTHORITY_LETTERS)
        .required(errorMessages.FIELD_REQUIRED);
    } else if (
      docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianForeignPassport]) ||
      docType?.value ===
        String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassportPlastic])
    ) {
      return schema
        .matches(
          regularExpressions.AUTHORITY_FOUR_DIGITS,
          errorMessages.DOCUMENT_AUTHORITY_FOUR_DIGITS
        )
        .required(errorMessages.FIELD_REQUIRED);
    }
    return schema.required(errorMessages.FIELD_REQUIRED);
  }
);

export const documentExpiryDateSchema = Yup.date()
  .transform(transformDate)
  .typeError(errorMessages.CORRECT_DATE_FORMAT)
  .when(
    ['documentType', 'issueDate', 'birthDate'],
    ([documentType, issueDate, birthDate], schema: Yup.DateSchema) => {
      const docType = documentType as TAutocompleteOption | null;
      const issue = issueDate as Date | undefined;
      const birth = birthDate as Date | undefined;

      if (
        docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianForeignPassport]) ||
        docType?.value ===
          String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassportPlastic])
      ) {
        const birthDateObj = birth ? new Date(birth) : null;
        const today = new Date();
        let age = 16;

        if (birthDateObj) {
          age = today.getFullYear() - birthDateObj.getFullYear();
          const m = today.getMonth() - birthDateObj.getMonth();
          if (m < 0 || (m === 0 && today.getDate() < birthDateObj.getDate())) {
            age--;
          }
        }

        const requiredYears = age >= 16 ? 10 : 4;

        return schema.test(
          'expiry-date-validation',
          `Строк дії паспорта має бути не менше ${requiredYears} років`,
          function (this: Yup.TestContext, expiryDate: Date | undefined) {
            if (!issue || !expiryDate) return false;

            const issueDateObj = new Date(issue);
            const expiry = new Date(expiryDate);

            const minValidExpiryDate = new Date(issueDateObj);
            minValidExpiryDate.setFullYear(issueDateObj.getFullYear() + requiredYears);

            const maxValidExpiryDate = new Date(issueDateObj);
            maxValidExpiryDate.setFullYear(issueDateObj.getFullYear() + 10);

            const nextDayAfterExpiry = new Date(maxValidExpiryDate);
            nextDayAfterExpiry.setDate(nextDayAfterExpiry.getDate() + 1);

            const isValid = expiry >= minValidExpiryDate && expiry <= maxValidExpiryDate;

            if (expiry < minValidExpiryDate) {
              return this.createError({
                message: errorMessages.PASSPORT_EXPIRY_TOO_SHORT,
              });
            }

            if (expiry >= nextDayAfterExpiry) {
              return this.createError({
                message: errorMessages.PASSPORT_EXPIRY_TOO_LONG,
              });
            }

            return isValid;
          }
        );
      }
      return schema.required(errorMessages.FIELD_REQUIRED);
    }
  )
  .required(errorMessages.FIELD_REQUIRED);

export const registryRecordSchema = Yup.string().when(
  ['documentType'],
  ([documentType], schema: Yup.StringSchema) => {
    const docType = documentType as TAutocompleteOption | null;
    if (docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassport])) {
      return schema
        .matches(regularExpressions.REGISTRY_RECORD_OPTIONAL, errorMessages.REGISTER_ENTRY_FORMAT)
        .nullable();
    } else if (
      docType?.value === String(generalInfoDoc[IdentityDocumentType.ukrainianForeignPassport]) ||
      docType?.value ===
        String(generalInfoDoc[IdentityDocumentType.ukrainianCitizenPassportPlastic])
    ) {
      return schema
        .matches(regularExpressions.REGISTRY_RECORD, errorMessages.REGISTER_ENTRY_FORMAT)
        .required(errorMessages.FIELD_REQUIRED);
    }
    return schema.nullable();
  }
);

export const createCounterpartyNameSchema = (condition: () => boolean): Yup.StringSchema => {
  return Yup.string().when([], {
    is: condition,
    then: (schema) => schema.required(errorMessages.FIELD_REQUIRED),
    otherwise: (schema) => schema.notRequired(),
  });
};

export const createCounterpartyCodeSchema = (
  condition: () => boolean,
  options: { withValidation?: boolean; withLengthValidation?: boolean } = {}
): Yup.StringSchema => {
  const { withValidation = false, withLengthValidation = false } = options;

  return Yup.string().when([], {
    is: condition,
    then: (schema) => {
      let validatedSchema = schema.required(errorMessages.FIELD_REQUIRED);

      if (withValidation || withLengthValidation) {
        validatedSchema = validatedSchema.matches(
          /^\d+$/,
          withValidation
            ? errorMessages.FIELD_NEED_ONLY_NUMER
            : 'ЄДРПОУ/РНОКПП повинен містити тільки цифри'
        );
      }

      if (withLengthValidation) {
        validatedSchema = validatedSchema.test(
          'length',
          'ЄДРПОУ/РНОКПП повинен містити 8 або 10 цифр',
          (value) => !value || [8, 10].includes(value.length)
        );
      }

      return validatedSchema;
    },
    otherwise: (schema) => schema.notRequired(),
  });
};

export const baseDocumentFieldsSchema = {
  lastName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  firstName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  middleName: Yup.string().required(errorMessages.FIELD_REQUIRED),
  identificationNumber: baseIdentificationNumberSchema,
  birthDate: requiredDateSchema,
  documentType: requiredOptionSchema,
  documentSeries: documentSeriesSchema,
  documentNumber: documentNumberSchema,
  issueDate: requiredDateSchema,
  issuingAuthority: issuingAuthoritySchema,
  documentExpiryDate: documentExpiryDateSchema,
  registryRecord: registryRecordSchema,
};
